import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const GROUPS = [
  { module: "Customers", action: "customers.edit", label: "Edit customer" },
  { module: "Customers", action: "customers.delete", label: "Delete customer" },
  { module: "Pets", action: "pets.edit", label: "Edit pet" },
  { module: "Pets", action: "pets.delete", label: "Delete pet" },
  { module: "Bookings", action: "bookings.delete", label: "Delete booking" },
  { module: "Packages", action: "package_plans.delete", label: "Delete package plan" },
  { module: "Inventory", action: "inventory.delete", label: "Delete / archive inventory" },
  { module: "Invoices", action: "invoices.cancel", label: "Cancel invoice" },
  { module: "Payments", action: "payments.refund", label: "Refund payment" },
];

export default function ApprovalSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = !user || hasPerm(user, "settings.edit");

  const { data: policy, isLoading } = useQuery({
    queryKey: ["approval-policy"], queryFn: async () => (await api.get("/approvals/policy")).data, enabled: canManage,
  });
  const save = useMutation({
    mutationFn: (next) => api.put("/approvals/policy", { policy: next }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["approval-policy"] }); toast.success("Approval policy updated"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  if (user && !canManage) return <Navigate to="/dashboard" replace />;
  if (isLoading) return <Loading />;

  const toggle = (action, on) => save.mutate({ ...(policy || {}), [action]: on });

  return (
    <div>
      <PageHeader title="Approvals & Controls" subtitle="Choose which sensitive actions require your approval before they run" />
      <div className="rounded-2xl border border-pawkt-border bg-white divide-y divide-slate-100" data-testid="approval-policy-list">
        {GROUPS.map((g) => (
          <div key={g.action} className="flex items-center justify-between gap-4 px-4 py-4" data-testid={`policy-row-${g.action}`}>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-pawkt-text-secondary">{g.module}</div>
              <div className="text-sm font-semibold text-pawkt-text">{g.label}</div>
            </div>
            <Switch checked={(policy || {})[g.action] !== false} onCheckedChange={(v) => toggle(g.action, v)}
              disabled={save.isPending} data-testid={`policy-toggle-${g.action}`} />
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-pawkt-text-disabled max-w-xl">
        When ON, staff who lack the direct permission must request your approval before this action runs. Turning it OFF does not grant anyone new permission — it simply removes the request path, so staff without the direct permission still cannot perform the action.
      </p>
    </div>
  );
}
